import React, { useMemo } from 'react';
import { Clock, TrendingUp, TrendingDown, DollarSign, Calendar, User, MapPin, Plus, AlertTriangle, CheckCircle, Play, ExternalLink, FileText, CreditCard as Edit2, X, Users, Search, Laptop, UserPlus, Building2, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Customer, Assignment, TimeEntry } from '../types';
import { formatHoursMinutes, calculateHoursFromTimes } from '../utils/timeUtils';
import { supabase } from '../lib/supabase';
import { EditEntryModal } from './EditEntryModal';
import { useAuth } from '../hooks/useAuth';

interface UserSettings {
  low_hours_threshold: number;
  show_low_hours_warning: boolean;
  show_active_timer_alert: boolean;
  show_duplicate_warning: boolean;
  dashboard_assignment_sort: string;
  assignment_manager_sort: string;
}

interface DashboardProps {
  customers: Customer[];
  timeEntries: TimeEntry[];
  assignments: Assignment[];
  userSettings?: UserSettings;
  onUpdateUserSettings?: (updates: Partial<UserSettings>) => void;
  onNavigateToRegister: () => void;
  onNavigateToCustomers?: () => void;
  onNavigateToHardware?: () => void;
  onShowNewCustomerForm?: (prefillName?: string) => void;
  onNavigateToCustomerWithHardware?: (customerId: string, hardwareId: string) => void;
  onUpdateTimeEntry: (id: string, updates: Partial<TimeEntry>) => void;
  onDeleteTimeEntry: (id: string) => void;
  onUpdateAssignment?: (id: string, updates: Partial<Assignment>) => void;
  onAddAssignment?: (assignment: Omit<Assignment, 'id'>) => Promise<Assignment | undefined>;
  onRefreshCustomers?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = React.memo(({
  customers,
  timeEntries,
  assignments,
  userSettings,
  onUpdateUserSettings,
  onNavigateToRegister,
  onNavigateToCustomers,
  onNavigateToHardware,
  onShowNewCustomerForm,
  onNavigateToCustomerWithHardware,
  onUpdateTimeEntry,
  onDeleteTimeEntry,
  onUpdateAssignment,
  onAddAssignment,
  onRefreshCustomers
}) => {
  const { user, isExternalPartner, canSeeAllAssignments } = useAuth();
  const [allocatedAssignmentIds, setAllocatedAssignmentIds] = React.useState<string[]>([]);
  const [activeTimer, setActiveTimer] = React.useState<any>(null);
  const [systemStartDate, setSystemStartDate] = React.useState<string | null>(null);
  const [daysActive, setDaysActive] = React.useState<number>(0);
  const [editingEntry, setEditingEntry] = React.useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = React.useState<Partial<TimeEntry>>({});
  const [sparePartsHistory, setSparePartsHistory] = React.useState<any[]>([]);
  const [availableArticles, setAvailableArticles] = React.useState<any[]>([]);
  const [showUnifiedSearch, setShowUnifiedSearch] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [customerResults, setCustomerResults] = React.useState<Customer[]>([]);
  const [hardwareResults, setHardwareResults] = React.useState<any[]>([]);
  const [selectedHardwareCustomer, setSelectedHardwareCustomer] = React.useState<Customer | null>(null);
  const [selectedHardware, setSelectedHardware] = React.useState<any | null>(null);
  const [totalHardwareCount, setTotalHardwareCount] = React.useState<number>(0);
  const [showQuickAssignmentModal, setShowQuickAssignmentModal] = React.useState(false);
  const [showCreateAssignmentModal, setShowCreateAssignmentModal] = React.useState(false);
  const [quickAssignmentSearchQuery, setQuickAssignmentSearchQuery] = React.useState('');
  const [newAssignmentForm, setNewAssignmentForm] = React.useState({
    name: '',
    description: '',
    caseNumber: '',
    hardwareId: ''
  });
  const customerActivityScores = useMemo(() => {
    const scoresMap = new Map<string, number>();
    const customerAssignments = new Map<string, { active: number; completed: number }>();
    const customerEntries = new Map<string, number>();

    for (const a of assignments) {
      if (!a.customerId) continue;
      const existing = customerAssignments.get(a.customerId) || { active: 0, completed: 0 };
      if (a.status === 'active') existing.active++;
      else if (a.status === 'completed') existing.completed++;
      customerAssignments.set(a.customerId, existing);
    }

    for (const te of timeEntries) {
      if (!te.customerId) continue;
      customerEntries.set(te.customerId, (customerEntries.get(te.customerId) || 0) + 1);
    }

    for (const c of customers) {
      const assignStats = customerAssignments.get(c.id) || { active: 0, completed: 0 };
      const entryCount = customerEntries.get(c.id) || 0;
      const score = assignStats.active * 10 + assignStats.completed * 5 + entryCount * 2;
      if (score > 0) scoresMap.set(c.id, score);
    }

    return scoresMap;
  }, [customers, assignments, timeEntries]);
  const [hoveredAssignmentId, setHoveredAssignmentId] = React.useState<string | null>(null);
  const [expandedMobileAssignmentId, setExpandedMobileAssignmentId] = React.useState<string | null>(null);
  const [sortBy, setSortBy] = React.useState<'date-desc' | 'date-asc' | 'hours-zero' | 'hours-zero-oldest' | 'hours-desc' | 'hours-asc'>(
    (userSettings?.dashboard_assignment_sort as any) || 'date-desc'
  );

  // Update sortBy when userSettings changes
  React.useEffect(() => {
    if (userSettings?.dashboard_assignment_sort) {
      setSortBy(userSettings.dashboard_assignment_sort as any);
    }
  }, [userSettings?.dashboard_assignment_sort]);

  // Save sortBy to settings when it changes
  React.useEffect(() => {
    if (onUpdateUserSettings && sortBy && userSettings?.dashboard_assignment_sort !== sortBy) {
      onUpdateUserSettings({ dashboard_assignment_sort: sortBy });
    }
  }, [sortBy, onUpdateUserSettings]);

  // Featured customers state
  const [selectedFeaturedCustomer, setSelectedFeaturedCustomer] = React.useState<'office' | 'reachtech' | 'helkom'>('office');
  const [completedPeriod, setCompletedPeriod] = React.useState<'all' | '7days' | '30days' | '90days' | 'year'>(
    (userSettings?.completed_period as any) || 'all'
  );

  // Update completedPeriod when userSettings changes
  React.useEffect(() => {
    if (userSettings?.completed_period) {
      setCompletedPeriod(userSettings.completed_period as any);
    }
  }, [userSettings?.completed_period]);

  // Save completedPeriod to settings when it changes
  React.useEffect(() => {
    if (onUpdateUserSettings && completedPeriod && userSettings?.completed_period !== completedPeriod) {
      onUpdateUserSettings({ completed_period: completedPeriod });
    }
  }, [completedPeriod, onUpdateUserSettings]);

  const [featuredCustomers, setFeaturedCustomers] = React.useState<{
    office: {
      customer: Customer | null;
      assignments: Array<Assignment & { totalMinutes: number }>;
      stats: { active: number; completed: number; avgMinutes: number };
    };
    reachtech: {
      customer: Customer | null;
      assignments: Array<Assignment & { totalMinutes: number }>;
      stats: { active: number; completed: number; avgMinutes: number };
    };
    helkom: {
      customer: Customer | null;
      assignments: Array<Assignment & { totalMinutes: number }>;
      stats: { active: number; completed: number; avgMinutes: number };
    };
  }>({
    office: {
      customer: null,
      assignments: [],
      stats: { active: 0, completed: 0, avgMinutes: 0 }
    },
    reachtech: {
      customer: null,
      assignments: [],
      stats: { active: 0, completed: 0, avgMinutes: 0 }
    },
    helkom: {
      customer: null,
      assignments: [],
      stats: { active: 0, completed: 0, avgMinutes: 0 }
    }
  });

  const [weeklyJobStats, setWeeklyJobStats] = React.useState<Array<{ weekStart: string; jobCount: number; totalMinutes: number }>>([]);


  React.useEffect(() => {
    const abortController = new AbortController();

    const loadSystemStartDate = async () => {
      try {
        if (abortController.signal.aborted) return;

        const { data, error } = await supabase
          .from('assignments')
          .select('created_at')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (abortController.signal.aborted) return;

        if (error) {
          console.error('Error loading system start date:', error);
          return;
        }

        if (data && data.created_at) {
          const startDate = new Date(data.created_at);

          // Validate that the date is valid
          if (!isNaN(startDate.getTime())) {
            setSystemStartDate(startDate.toLocaleDateString('sv-SE'));

            const today = new Date();
            const diffTime = Math.abs(today.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setDaysActive(diffDays);
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || abortController.signal.aborted) return;
        console.error('Could not load system start date:', error);
      }
    };

    loadSystemStartDate();

    return () => {
      abortController.abort();
    };
  }, []);


  React.useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const loadAllocatedAssignments = async () => {
      if (isExternalPartner && user) {
        try {
          const { data, error } = await supabase
            .from('user_assignment_allocations')
            .select('assignment_id')
            .eq('user_id', user.id);

          if (!isMounted || abortController.signal.aborted) return;

          if (error) {
            console.error('Error loading allocated assignments:', error);
            return;
          }

          if (data) {
            setAllocatedAssignmentIds(data.map(a => a.assignment_id));
          }
        } catch (error: any) {
          if (error.name === 'AbortError' || !isMounted) return;
          console.error('Error loading allocated assignments:', error);
        }
      }
    };

    const loadAvailableArticles = async () => {
      try {
        const { data, error } = await supabase
          .from('articles')
          .select('id, article_number, name, description, recommended_price, stock_quantity')
          .order('article_number');

        if (!isMounted || abortController.signal.aborted) return;

        if (error) throw error;
        setAvailableArticles(data || []);
      } catch (error: any) {
        if (error.name === 'AbortError' || !isMounted) return;
        console.error('Error loading articles:', error);
      }
    };

    const loadHardwareCount = async () => {
      try {
        const { count, error } = await supabase
          .from('hardware')
          .select('*', { count: 'exact', head: true });

        if (!isMounted || abortController.signal.aborted) return;

        if (error) throw error;
        setTotalHardwareCount(count || 0);
      } catch (error: any) {
        if (error.name === 'AbortError' || !isMounted) return;
        console.error('Error loading hardware count:', error);
      }
    };

    loadAllocatedAssignments();
    loadAvailableArticles();
    loadHardwareCount();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [isExternalPartner, user]);

  // Load spare parts history when editing an entry
  React.useEffect(() => {
    const abortController = new AbortController();

    if (editingEntry) {
      const assignment = assignments.find(a => a.id === editingEntry.assignmentId);

      // Parse spare parts from entry
      let spareParts: Array<{quantity?: string; partNumber?: string; description?: string}> = [];
      if (editingEntry.spareParts) {
        try {
          const parsed = typeof editingEntry.spareParts === 'string'
            ? JSON.parse(editingEntry.spareParts)
            : editingEntry.spareParts;
          spareParts = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('Error parsing spare parts:', e);
        }
      }

      const formData = {
        ...editingEntry,
        assignmentStatus: assignment?.status || 'active',
        spareParts
      };
      setEditForm(formData as any);
      loadSparePartsHistory(editingEntry.customerId, abortController.signal);
    }

    return () => {
      abortController.abort();
    };
  }, [editingEntry, assignments]);

  const loadSparePartsHistory = async (customerId: string | null | undefined, signal?: AbortSignal) => {
    try {
      if (signal?.aborted) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user || signal?.aborted) {
        setSparePartsHistory([]);
        return;
      }

      const { data, error } = await supabase
        .from('spare_parts_history')
        .select('*')
        .eq('user_id', user.id)
        .order('use_count', { ascending: false })
        .order('last_used', { ascending: false })
        .limit(10);

      if (signal?.aborted) return;

      if (error) throw error;
      setSparePartsHistory(data || []);
    } catch (error: any) {
      if (error.name === 'AbortError' || signal?.aborted) return;
      console.error('Error loading spare parts history:', error);
      setSparePartsHistory([]);
    }
  };

  // Helper function to get date threshold based on period
  const getCompletedDateThreshold = (period: 'all' | '7days' | '30days' | '90days' | 'year'): Date | null => {
    if (period === 'all') return null;

    const now = new Date();
    switch (period) {
      case '7days':
        now.setDate(now.getDate() - 7);
        break;
      case '30days':
        now.setDate(now.getDate() - 30);
        break;
      case '90days':
        now.setDate(now.getDate() - 90);
        break;
      case 'year':
        now.setFullYear(now.getFullYear() - 1);
        break;
    }
    return now;
  };

  // Load featured customer data (Office, Reachtech, or Helkom)
  const loadFeaturedCustomerData = async (customerType: 'office' | 'reachtech' | 'helkom', signal?: AbortSignal) => {
    try {
      if (signal?.aborted) return;

      const CUSTOMER_IDS = {
        office: '1ad09cf4-1787-4cdd-9481-ac5da373a5ca',
        reachtech: '', // Will be fetched dynamically
        helkom: '' // Will be fetched dynamically
      };

      let customerId = CUSTOMER_IDS[customerType];

      // If Reachtech or Helkom, find by name
      if (customerType === 'reachtech') {
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('id')
          .ilike('name', '%Reachtech%')
          .maybeSingle();

        if (signal?.aborted) return;
        if (customerError || !customerData) {
          console.warn('Reachtech customer not found');
          return;
        }
        customerId = customerData.id;
      } else if (customerType === 'helkom') {
        // Use Helkom i Borås AB directly
        customerId = 'cfc01f46-dd06-4c6b-ad4d-f962d3fcd21c';
      }

      // Load customer data
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .maybeSingle();

      if (signal?.aborted) return;
      if (customerError) throw customerError;

      let customer: Customer | null = null;
      if (customerData) {
        customer = {
          id: customerData.id,
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          remainingMinutes: Number(customerData.current_balance_minutes) || 0,
          totalMinutes: Number(customerData.total_purchased_minutes) || 0,
          status: customerData.status || 'active'
        };
      }

      // Load assignment statistics with time entries for average calculation
      const { data: allAssignmentsData, error: allAssignmentsError } = await supabase
        .from('assignments')
        .select(`
          id,
          status,
          updated_at,
          time_entries (
            work_time,
            travel_time,
            return_travel_time
          )
        `)
        .eq('customer_id', customerId);

      if (signal?.aborted) return;
      if (allAssignmentsError) throw allAssignmentsError;

      let stats = { active: 0, completed: 0, avgMinutes: 0 };
      if (allAssignmentsData) {
        const activeCount = allAssignmentsData.filter((a: any) => a.status === 'active').length;

        // Filter completed assignments based on selected period
        const dateThreshold = getCompletedDateThreshold(completedPeriod);
        let completedCount = 0;
        if (dateThreshold) {
          completedCount = allAssignmentsData.filter((a: any) =>
            a.status === 'completed' && new Date(a.updated_at) >= dateThreshold
          ).length;
        } else {
          completedCount = allAssignmentsData.filter((a: any) => a.status === 'completed').length;
        }

        // Calculate average time per assignment
        let totalMinutesAllAssignments = 0;
        let assignmentsWithTime = 0;

        allAssignmentsData.forEach((a: any) => {
          const assignmentMinutes = (a.time_entries || []).reduce((sum: number, te: any) => {
            return sum + (Number(te.work_time) || 0) + (Number(te.travel_time) || 0) + (Number(te.return_travel_time) || 0);
          }, 0);

          if (assignmentMinutes > 0) {
            totalMinutesAllAssignments += assignmentMinutes;
            assignmentsWithTime++;
          }
        });

        const avgMinutes = assignmentsWithTime > 0 ? Math.round(totalMinutesAllAssignments / assignmentsWithTime) : 0;
        stats = { active: activeCount, completed: completedCount, avgMinutes };
      }

      // Load active assignments with time totals
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('assignments')
        .select(`
          *,
          time_entries (
            work_time,
            travel_time,
            return_travel_time
          )
        `)
        .eq('customer_id', customerId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (signal?.aborted) return;
      if (assignmentsError) throw assignmentsError;

      let assignments: Array<Assignment & { totalMinutes: number }> = [];
      if (assignmentsData) {
        assignments = assignmentsData.map((a: any) => {
          const totalMinutes = (a.time_entries || []).reduce((sum: number, te: any) => {
            return sum + (Number(te.work_time) || 0) + (Number(te.travel_time) || 0) + (Number(te.return_travel_time) || 0);
          }, 0);

          return {
            id: a.id,
            name: a.name,
            description: a.description,
            status: a.status,
            customerId: a.customer_id,
            createdDate: a.created_date,
            isOngoing: a.is_ongoing,
            totalMinutes
          } as Assignment & { totalMinutes: number };
        });
      }

      // Update state
      setFeaturedCustomers(prev => ({
        ...prev,
        [customerType]: {
          customer,
          assignments,
          stats
        }
      }));

      // Load weekly job statistics for all customers
      const threeWeeksAgo = new Date();
      threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);

      const { data: entriesData, error: entriesError } = await supabase
        .from('time_entries')
        .select(`
          date,
          assignment_id,
          work_time,
          travel_time,
          return_travel_time
        `)
        .gte('date', threeWeeksAgo.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (!signal?.aborted && !entriesError && entriesData) {
        const weekMap = new Map<string, { assignmentIds: Set<string>; totalMinutes: number }>();

        entriesData.forEach((entry: any) => {
          const entryDate = new Date(entry.date);
          const weekStart = new Date(entryDate);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
          const weekKey = weekStart.toISOString().split('T')[0];

          if (!weekMap.has(weekKey)) {
            weekMap.set(weekKey, { assignmentIds: new Set(), totalMinutes: 0 });
          }
          const weekData = weekMap.get(weekKey)!;
          weekData.assignmentIds.add(entry.assignment_id);
          weekData.totalMinutes += (Number(entry.work_time) || 0) + (Number(entry.travel_time) || 0) + (Number(entry.return_travel_time) || 0);
        });

        const stats = Array.from(weekMap.entries())
          .map(([weekStart, weekData]) => ({
            weekStart,
            jobCount: weekData.assignmentIds.size,
            totalMinutes: weekData.totalMinutes
          }))
          .sort((a, b) => b.weekStart.localeCompare(a.weekStart))
          .slice(0, 3);

        setWeeklyJobStats(stats);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || signal?.aborted) return;
      console.error('Error loading Office data:', error);
    }
  };

  // Effect to load featured customer data
  React.useEffect(() => {
    const abortController = new AbortController();
    loadFeaturedCustomerData('office', abortController.signal);
    loadFeaturedCustomerData('reachtech', abortController.signal);
    loadFeaturedCustomerData('helkom', abortController.signal);

    const interval = setInterval(() => {
      loadFeaturedCustomerData('office', abortController.signal);
      loadFeaturedCustomerData('reachtech', abortController.signal);
      loadFeaturedCustomerData('helkom', abortController.signal);
    }, 60000);

    return () => {
      abortController.abort();
      clearInterval(interval);
    };
  }, [completedPeriod]);

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    try {
      const formData = editForm as any;

      // Validate non-billable reason if not billable
      if (formData.isBillable === false && (!formData.nonBillableReason || !formData.nonBillableReason.trim())) {
        alert('Du måste ange en anledning när tidsposten inte är fakturerbar');
        return;
      }

      // Ensure session is valid before attempting to save
      const { ensureValidSession } = await import('../utils/sessionManager');
      const sessionValid = await ensureValidSession();

      if (!sessionValid) {
        alert('Din session har gått ut. Ladda om sidan och logga in igen för att fortsätta.\n\nTips: För att undvika detta problem, se till att appen inte är inaktiv för länge.');
        window.location.reload();
        return;
      }

      // Handle assignment status update separately
      if (formData.assignmentStatus && editingEntry.assignmentId && onUpdateAssignment) {
        const assignment = assignments.find(a => a.id === editingEntry.assignmentId);
        if (assignment && assignment.status !== formData.assignmentStatus) {
          // Update assignment status using callback
          await onUpdateAssignment(editingEntry.assignmentId, {
            status: formData.assignmentStatus
          });

          // If assignment is marked as completed, stop any active timer for this assignment
          if (formData.assignmentStatus === 'completed') {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const { data: activeTimerData } = await supabase
                .from('active_timers')
                .select('*')
                .eq('user_id', user.id)
                .eq('assignment_id', editingEntry.assignmentId)
                .maybeSingle();

              if (activeTimerData) {
                // Remove from database
                await supabase
                  .from('active_timers')
                  .delete()
                  .eq('user_id', user.id);

                // Clear from state
                setActiveTimer(null);

                // Clear from localStorage
                localStorage.removeItem('activeTimer');
              }
            }
          }
        }
      }

      // Create missing articles in registry if spare parts are present
      if (formData.spareParts && Array.isArray(formData.spareParts)) {
        for (const article of formData.spareParts) {
          if (article.partNumber) {
            const exists = availableArticles.find(a => a.article_number === article.partNumber);
            if (!exists) {
              try {
                const { data, error } = await supabase
                  .from('articles')
                  .insert({
                    article_number: article.partNumber,
                    name: article.description || article.partNumber,
                    description: article.description || null,
                    recommended_price: article.price ? parseFloat(article.price) : null,
                    stock_quantity: 0,
                    min_stock_level: 0,
                    unit: 'st',
                    location: null,
                    notes: 'Skapad automatiskt från tidsregistrering'
                  })
                  .select()
                  .single();

                if (!error && data) {
                  setAvailableArticles(prev => [...prev, data]);
                }
              } catch (error) {
                console.error('Error creating article:', error);
              }
            }
          }
        }
      }

      // Prepare time entry update (remove assignmentStatus as it's not part of time_entries)
      const { assignmentStatus, ...timeEntryUpdates } = formData;

      // Filter spare parts if present
      if (timeEntryUpdates.spareParts && Array.isArray(timeEntryUpdates.spareParts)) {
        const validSpareParts = timeEntryUpdates.spareParts.filter((part: any) => part.partNumber && part.partNumber.trim());
        timeEntryUpdates.spareParts = validSpareParts.length > 0 ? validSpareParts : [];
      }

      await onUpdateTimeEntry(editingEntry.id, timeEntryUpdates);
      setEditingEntry(null);
      setEditForm({});
    } catch (error: any) {
      console.error('Error updating entry:', error);

      // Check if it's a session/auth error
      if (error?.message?.includes('JWT') || error?.message?.includes('session') || error?.message?.includes('auth')) {
        alert('Din session har gått ut. Ladda om sidan och logga in igen.\n\nTips: För att undvika detta problem, se till att appen inte är inaktiv för länge.');
        window.location.reload();
      } else {
        const errorMessage = error?.message || 'Okänt fel uppstod';
        alert(`Kunde inte spara ändringarna: ${errorMessage}\n\nKontrollera att alla fält är korrekt ifyllda och försök igen.`);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditForm({});
  };

  const handleFormChange = (updates: Partial<TimeEntry>) => {
    setEditForm(prev => ({ ...prev, ...updates }));
  };

  const handleUpdateAssignment = async (id: string, updates: Partial<Assignment>) => {
    try {
      const { error } = await supabase
        .from('assignments')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  React.useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const loadActiveTimer = async () => {
      try {
        // First try to load from cloud
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (!isMounted || abortController.signal.aborted) return;

        if (!userError && user) {
          const { data: cloudTimer, error: timerError } = await supabase
            .from('active_timers')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          if (!isMounted || abortController.signal.aborted) return;

          if (!timerError && cloudTimer) {
            // Cloud timer exists - use it
            const timer = {
              type: cloudTimer.timer_type,
              startTime: cloudTimer.start_time,
              startTimestamp: cloudTimer.start_timestamp,
              customerId: cloudTimer.customer_id,
              assignmentId: cloudTimer.assignment_id,
              customerName: cloudTimer.customer_name,
              assignmentName: cloudTimer.assignment_name
            };
            setActiveTimer(timer);
            // Also update localStorage
            localStorage.setItem('timbank_active_timer', JSON.stringify(timer));
            return;
          }
        }

        // Fallback to localStorage if no cloud timer
        const savedTimer = localStorage.getItem('timbank_active_timer');
        if (savedTimer && isMounted) {
          try {
            const timer = JSON.parse(savedTimer);
            setActiveTimer(timer);
          } catch (error) {
            console.error('Error parsing timer from localStorage:', error);
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || !isMounted) return;
        console.error('Error loading active timer:', error);
      }
    };

    loadActiveTimer();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  // Subscribe to real-time updates for active timers
  React.useEffect(() => {
    let subscription: any;

    const subscribeToTimers = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      subscription = supabase
        .channel('dashboard-active-timers')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'active_timers',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Dashboard timer update received:', payload);

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              const cloudTimer = payload.new;
              const timer = {
                type: cloudTimer.timer_type,
                startTime: cloudTimer.start_time,
                startTimestamp: cloudTimer.start_timestamp,
                customerId: cloudTimer.customer_id,
                assignmentId: cloudTimer.assignment_id,
                customerName: cloudTimer.customer_name,
                assignmentName: cloudTimer.assignment_name
              };
              setActiveTimer(timer);
              localStorage.setItem('timbank_active_timer', JSON.stringify(timer));
            } else if (payload.eventType === 'DELETE') {
              console.log('Timer deleted, clearing active timer');
              setActiveTimer(null);
              localStorage.removeItem('timbank_active_timer');
            }
          }
        )
        .subscribe();
    };

    subscribeToTimers();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, []);

  const searchAbortController = React.useRef<AbortController | null>(null);

  const handleUnifiedSearch = async (query: string) => {
    // Cancel previous search if still running
    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }

    setSearchQuery(query);
    if (query.trim().length < 2) {
      setCustomerResults([]);
      setHardwareResults([]);
      return;
    }

    const filteredCustomers = customers.filter(c =>
      c.name.toLowerCase().includes(query.toLowerCase())
    );
    setCustomerResults(filteredCustomers);

    // Create new abort controller for this search
    searchAbortController.current = new AbortController();
    const currentController = searchAbortController.current;

    try {
      const { data, error } = await supabase
        .from('hardware')
        .select('*')
        .or(`name.ilike.%${query}%,serial_number.ilike.%${query}%,manufacturer.ilike.%${query}%,model.ilike.%${query}%`)
        .limit(10);

      if (currentController.signal.aborted) return;

      if (error) throw error;
      setHardwareResults(data || []);
    } catch (error: any) {
      if (error.name === 'AbortError' || currentController.signal.aborted) return;
      console.error('Error searching hardware:', error);
      setHardwareResults([]);
    }
  };

  // Update elapsed time every second if there's an active timer
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  
  React.useEffect(() => {
    if (activeTimer) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [activeTimer]);

  // Calculate current elapsed time
  const currentElapsedTime = React.useMemo(() => {
    if (!activeTimer) return null;
    
    const elapsed = Math.floor((currentTime - activeTimer.startTimestamp) / 1000);
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const secs = elapsed % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, [activeTimer, currentTime]);

  const stats = useMemo(() => {
    const timeBankCustomers = customers.filter(c => c.billingType === 'time_package');
    const totalHours = timeBankCustomers.reduce((sum, c) => sum + (Number(c.totalPurchasedMinutes) || 0), 0);
    const usedHours = timeBankCustomers.reduce((sum, c) => sum + (Number(c.totalUsedMinutes) || 0), 0);
    const remainingHours = totalHours - usedHours;
    const activeCustomers = customers.filter(c => c.status === 'active').length;

    // Filter completed assignments based on selected period
    const dateThreshold = getCompletedDateThreshold(completedPeriod);
    let completedAssignments = 0;
    if (dateThreshold) {
      completedAssignments = assignments.filter(a =>
        a.status === 'completed' && a.updatedAt && new Date(a.updatedAt) >= dateThreshold
      ).length;
    } else {
      completedAssignments = assignments.filter(a => a.status === 'completed').length;
    }

    const activeAssignments = assignments.filter(a => a.status === 'active').length;
    const waitingForPartsAssignments = assignments.filter(a => a.status === 'waiting_parts').length;

    return {
      totalHours,
      usedHours,
      remainingHours,
      activeCustomers,
      totalAssignments: assignments.length,
      completedAssignments,
      activeAssignments,
      waitingForPartsAssignments,
      totalEntries: timeEntries.length
    };
  }, [customers, assignments, timeEntries, completedPeriod]);

  const filteredTimeEntries = useMemo(() => {
    if (canSeeAllAssignments) {
      return timeEntries;
    }

    return timeEntries.filter(entry => {
      const assignment = assignments.find(a => a.id === entry.assignmentId);
      if (!assignment) return false;

      return allocatedAssignmentIds.includes(assignment.id) ||
        (assignment.assigned_user_id && assignment.assigned_user_id === user?.id);
    });
  }, [timeEntries, assignments, canSeeAllAssignments, allocatedAssignmentIds, user]);

  const recentEntries = useMemo(() => {
    return filteredTimeEntries
      .filter(entry => entry.date && !isNaN(new Date(entry.date).getTime()))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [filteredTimeEntries]);

  const lowHourCustomers = useMemo(() => {
    const threshold = (userSettings?.low_hours_threshold ?? 5) * 60;
    return customers.filter(c => {
      const remaining = (Number(c.currentBalanceMinutes) || 0);
      return c.billingType === 'time_package' && c.status === 'active' && remaining < threshold && remaining > 0;
    });
  }, [customers, userSettings]);

  const todaysEntries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return filteredTimeEntries
      .filter(entry => {
        if (!entry.date) return false;
        const entryDate = new Date(entry.date);
        entryDate.setHours(0, 0, 0, 0);
        return entryDate.getTime() === today.getTime();
      })
      .sort((a, b) => {
        // Sort by earliest time (travel start or work start), earliest first
        const aTime = a.travelStartTime || a.workStartTime || '23:59';
        const bTime = b.travelStartTime || b.workStartTime || '23:59';
        return aTime.localeCompare(bTime);
      });
  }, [filteredTimeEntries]);

  const todaysJobsSummary = useMemo(() => {
    const jobsMap = new Map<string, {
      assignment: Assignment;
      customer: Customer | undefined;
      entries: TimeEntry[];
      totalMinutes: number;
    }>();

    todaysEntries.forEach(entry => {
      const assignment = assignments.find(a => a.id === entry.assignmentId);
      if (!assignment) return;

      const customer = customers.find(c => c.id === assignment.customerId);

      const workTime = entry.workStartTime && entry.workEndTime
        ? calculateHoursFromTimes(entry.workStartTime, entry.workEndTime)
        : (Number(entry.workTime) || 0);
      const travelTime = entry.travelStartTime && entry.travelEndTime
        ? calculateHoursFromTimes(entry.travelStartTime, entry.travelEndTime)
        : (Number(entry.travelTime) || 0);
      const returnTravelTime = Number(entry.returnTravelTime) || 0;
      const totalMinutes = workTime + travelTime + returnTravelTime;

      if (jobsMap.has(assignment.id)) {
        const existing = jobsMap.get(assignment.id)!;
        existing.entries.push(entry);
        existing.totalMinutes += totalMinutes;
      } else {
        jobsMap.set(assignment.id, {
          assignment,
          customer,
          entries: [entry],
          totalMinutes
        });
      }
    });

    return Array.from(jobsMap.values()).sort((a, b) => {
      // Sort by earliest time across all entries in the job
      const getEarliestTime = (entries: TimeEntry[]) => {
        const times = entries.map(entry => entry.travelStartTime || entry.workStartTime || '23:59');
        return times.sort()[0] || '23:59';
      };

      const aTime = getEarliestTime(a.entries);
      const bTime = getEarliestTime(b.entries);
      return aTime.localeCompare(bTime);
    });
  }, [todaysEntries, assignments, customers]);

  const activeCustomers = customers
    .filter(c => c.status === 'active')
    .sort((a, b) => {
      const aEntries = timeEntries.filter(e => e.customerId === a.id);
      const bEntries = timeEntries.filter(e => e.customerId === b.id);

      const aAssignments = assignments.filter(ass => ass.customerId === a.id && ass.status === 'active').length;
      const bAssignments = assignments.filter(ass => ass.customerId === b.id && ass.status === 'active').length;

      const aScore = aEntries.length * 10 + aAssignments * 5;
      const bScore = bEntries.length * 10 + bAssignments * 5;

      if (aScore !== bScore) {
        return bScore - aScore;
      }

      const aLastActivity = aEntries.length > 0
        ? Math.max(...aEntries.map(e => new Date(e.date).getTime()))
        : 0;
      const bLastActivity = bEntries.length > 0
        ? Math.max(...bEntries.map(e => new Date(e.date).getTime()))
        : 0;

      return bLastActivity - aLastActivity;
    })
    .slice(0, 5);

  return (
    <div className="space-y-4">
      {/* 4-Column Dashboard Layout */}
      <div className="hidden lg:grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Column 1: Dashboard & Quick Actions */}
        <div className="bg-white shadow rounded-lg p-4 flex flex-col space-y-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900">TimBank</h1>
            {systemStartDate && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-gray-600">
                  Systemstart: <span className="font-semibold text-gray-900">{systemStartDate}</span>
                </p>
                <p className="text-xs text-gray-600">
                  Dagar i drift: <span className="font-semibold text-gray-900">{daysActive} dagar</span>
                </p>
              </div>
            )}
          </div>

          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-2">Snabbknappar</h2>
            <div className="space-y-1.5">
              {/* Kundregister */}
              {onNavigateToCustomers && (
                <button
                  onClick={onNavigateToCustomers}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-all text-left"
                >
                  <Users className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-900">Kundregister</span>
                </button>
              )}

              {/* Nytt Uppdrag */}
              <button
                onClick={() => setShowQuickAssignmentModal(true)}
                className="w-full flex items-center space-x-2 px-3 py-2 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-md transition-all text-left"
              >
                <FileText className="h-4 w-4 text-cyan-600 flex-shrink-0" />
                <span className="text-xs font-medium text-cyan-900">Nytt Uppdrag</span>
              </button>

              {/* Sök */}
              <button
                onClick={() => {
                  setShowUnifiedSearch(true);
                  setSearchQuery('');
                  setCustomerResults([]);
                  setHardwareResults([]);
                }}
                className="w-full flex items-center space-x-2 px-3 py-2 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-md transition-all text-left"
              >
                <Search className="h-4 w-4 text-pink-600 flex-shrink-0" />
                <span className="text-xs font-medium text-pink-900">Sök</span>
              </button>

              {/* Hårdvara */}
              {onNavigateToHardware && (
                <button
                  onClick={onNavigateToHardware}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-md transition-all text-left"
                >
                  <Laptop className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-orange-900">Hårdvara</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Featured Customer Stats */}
        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100">
          {/* Header with dropdown */}
          <div className="bg-blue-50 px-3 py-2 flex items-center justify-between border-b border-blue-100">
            <div className="flex items-center space-x-2 flex-1">
              <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <select
                value={selectedFeaturedCustomer}
                onChange={(e) => setSelectedFeaturedCustomer(e.target.value as 'office' | 'reachtech' | 'helkom')}
                className="text-sm font-bold text-gray-900 bg-blue-50 border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer hover:bg-blue-100 transition-colors"
              >
                <option value="office">Office Sverige AB</option>
                <option value="reachtech">Reachtech AB</option>
                <option value="helkom">Helkom</option>
              </select>
            </div>
          </div>

          {featuredCustomers[selectedFeaturedCustomer].customer ? (
            <div className="p-3">
              {/* Timebank Status */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-600">Timbank</span>
                  <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-0.5">
                  {formatHoursMinutes(featuredCustomers[selectedFeaturedCustomer].customer!.remainingMinutes)}
                </div>
                <div className="text-xs text-gray-500">
                  av {formatHoursMinutes(featuredCustomers[selectedFeaturedCustomer].customer!.totalMinutes)} totalt
                </div>
              </div>

              {/* Assignment Statistics */}
              <div className="pt-3 border-t border-blue-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-0.5">Aktiva uppdrag</div>
                    <div className="text-xl font-bold text-green-600">{featuredCustomers[selectedFeaturedCustomer].stats.active}</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="text-xs font-medium text-gray-600">Klara uppdrag</div>
                      <select
                        value={completedPeriod}
                        onChange={(e) => setCompletedPeriod(e.target.value as any)}
                        className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">Alla</option>
                        <option value="7days">7 dagar</option>
                        <option value="30days">30 dagar</option>
                        <option value="90days">90 dagar</option>
                        <option value="year">1 år</option>
                      </select>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{featuredCustomers[selectedFeaturedCustomer].stats.completed}</div>
                  </div>
                </div>
                <div className="pt-2 border-t border-blue-100">
                  <div className="text-xs font-medium text-gray-600 mb-0.5">Snitt tid per uppdrag</div>
                  <div className="text-xl font-bold text-blue-600">{formatHoursMinutes(featuredCustomers[selectedFeaturedCustomer].stats.avgMinutes)}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-1"></div>
                <div className="text-xs">Laddar kunddata...</div>
              </div>
            </div>
          )}
        </div>

        {/* Column 3: Weekly Job Statistics */}
        <div className="bg-green-50 shadow rounded-lg p-4 flex flex-col space-y-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-green-600" />
            <h2 className="text-sm font-bold text-gray-900">Senaste 3 veckor</h2>
          </div>

          {weeklyJobStats.length > 0 ? (
            <>
              <div className="space-y-2">
                {weeklyJobStats.map((week, index) => {
                  const weekDate = new Date(week.weekStart);
                  const weekEndDate = new Date(weekDate);
                  weekEndDate.setDate(weekEndDate.getDate() + 6);

                  const prevWeek = weeklyJobStats[index + 1];
                  const trend = prevWeek
                    ? week.jobCount > prevWeek.jobCount ? 'up'
                    : week.jobCount < prevWeek.jobCount ? 'down'
                    : 'same'
                    : null;

                  const trendPercent = prevWeek
                    ? Math.abs(Math.round(((week.jobCount - prevWeek.jobCount) / prevWeek.jobCount) * 100))
                    : 0;

                  return (
                    <div
                      key={week.weekStart}
                      className="bg-white rounded-lg p-3 shadow hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs font-semibold text-gray-700">
                            Vecka {index === 0 ? '(denna vecka)' : index === 1 ? '(förra veckan)' : ''}
                          </div>
                          <div className="text-xs text-gray-500">
                            {weekDate.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} - {weekEndDate.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="flex items-baseline space-x-1">
                            <div className="text-lg font-bold text-gray-900">
                              {week.jobCount}
                            </div>
                            <div className="text-xs text-gray-600">
                              jobb
                            </div>
                            <div className="text-xs text-gray-600">
                              {formatHoursMinutes(week.totalMinutes)}
                            </div>
                          </div>
                          {trend && (
                            <div className={`flex items-center text-xs font-medium mt-0.5 ${
                              trend === 'up' ? 'text-green-600' :
                              trend === 'down' ? 'text-red-600' :
                              'text-gray-600'
                            }`}>
                              {trend === 'up' && <ArrowUpRight className="h-3 w-3" />}
                              {trend === 'down' && <ArrowDownRight className="h-3 w-3" />}
                              {trend === 'same' && <Minus className="h-3 w-3" />}
                              {trendPercent > 0 && <span className="ml-0.5">{trendPercent}%</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-1"></div>
                <div className="text-xs">Laddar statistik...</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unified Search Modal */}
      {showUnifiedSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Sök Kund eller Hårdvara</h3>
              <button
                onClick={() => {
                  setShowUnifiedSearch(false);
                  setSearchQuery('');
                  setCustomerResults([]);
                  setHardwareResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                placeholder="Sök efter kund eller hårdvara..."
                value={searchQuery}
                onChange={(e) => handleUnifiedSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {searchQuery.length >= 2 ? (
                <div className="space-y-4">
                  {/* Customer Results */}
                  {customerResults.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Users className="h-4 w-4 mr-2 text-blue-600" />
                        Kunder ({customerResults.length})
                      </h4>
                      <div className="space-y-2">
                        {customerResults.map((customer: Customer) => (
                          <button
                            key={customer.id}
                            onClick={() => {
                              setShowUnifiedSearch(false);
                              if (onNavigateToCustomers) {
                                onNavigateToCustomers();
                              }
                            }}
                            className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{customer.name}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {formatHoursMinutes(customer.remainingMinutes)} kvar av {formatHoursMinutes(customer.totalMinutes)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hardware Results */}
                  {hardwareResults.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Laptop className="h-4 w-4 mr-2 text-orange-600" />
                        Hårdvara ({hardwareResults.length})
                      </h4>
                      <div className="space-y-2">
                        {hardwareResults.map((hardware: any) => {
                          const hardwareCustomer = hardware.owner_customer_id
                            ? customers.find(c => c.id === hardware.owner_customer_id)
                            : hardware.billing_customer_id
                            ? customers.find(c => c.id === hardware.billing_customer_id)
                            : null;

                          return (
                            <button
                              key={hardware.id}
                              onClick={async () => {
                                setShowUnifiedSearch(false);
                                if (hardwareCustomer) {
                                  setSelectedHardwareCustomer(hardwareCustomer);
                                  setSelectedHardware(hardware);
                                }
                              }}
                              className="w-full text-left p-3 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors"
                            >
                              <div className="font-medium text-gray-900">{hardware.name}</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {hardware.manufacturer && hardware.model && `${hardware.manufacturer} ${hardware.model}`}
                                {hardware.serial_number && ` • SN: ${hardware.serial_number}`}
                                {hardwareCustomer && (
                                  <span className="ml-2 text-blue-600">• {hardwareCustomer.name}</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {customerResults.length === 0 && hardwareResults.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">Inga resultat hittades för "{searchQuery}"</p>
                      <div className="space-y-2">
                        {onShowNewCustomerForm && (
                          <button
                            onClick={() => {
                              setShowUnifiedSearch(false);
                              onShowNewCustomerForm(searchQuery);
                            }}
                            className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors mx-2"
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Skapa ny kund
                          </button>
                        )}
                        {onNavigateToHardware && (
                          <button
                            onClick={() => {
                              setShowUnifiedSearch(false);
                              onNavigateToHardware();
                            }}
                            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors mx-2"
                          >
                            <Laptop className="h-4 w-4 mr-2" />
                            Registrera ny hårdvara
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-gray-500">Skriv minst 2 tecken för att söka</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Card Modal for Hardware */}
      {selectedHardwareCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Kundkort</h3>
              <button
                onClick={() => setSelectedHardwareCustomer(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Customer Info */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{selectedHardwareCustomer.name}</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className={`ml-2 font-medium ${
                        selectedHardwareCustomer.status === 'active' ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {selectedHardwareCustomer.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Faktureringstyp:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {selectedHardwareCustomer.billing_type === 'time_bank' ? 'Timbank' :
                         selectedHardwareCustomer.billing_type === 'per_job' ? 'Per Jobb' : 'Fast pris'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Time Bank Info */}
                {selectedHardwareCustomer.billing_type === 'time_bank' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-semibold text-blue-900 mb-2">Timbank</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Total tid:</span>
                        <span className="font-medium text-blue-900">
                          {formatHoursMinutes(selectedHardwareCustomer.totalMinutes)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Använt:</span>
                        <span className="font-medium text-blue-900">
                          {formatHoursMinutes(selectedHardwareCustomer.totalMinutes - selectedHardwareCustomer.remainingMinutes)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Kvar:</span>
                        <span className="font-bold text-blue-900">
                          {formatHoursMinutes(selectedHardwareCustomer.remainingMinutes)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hardware List */}
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Laptop className="h-5 w-5 mr-2 text-orange-600" />
                    Hårdvara kopplad till kunden
                  </h5>
                  <div className="space-y-2">
                    {hardwareResults
                      .filter(hw =>
                        hw.owner_customer_id === selectedHardwareCustomer.id ||
                        hw.billing_customer_id === selectedHardwareCustomer.id
                      )
                      .map(hardware => (
                        <div
                          key={hardware.id}
                          className="p-3 bg-orange-50 border border-orange-200 rounded-lg"
                        >
                          <div className="font-medium text-gray-900">{hardware.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {hardware.manufacturer && hardware.model && `${hardware.manufacturer} ${hardware.model}`}
                            {hardware.serial_number && ` • SN: ${hardware.serial_number}`}
                          </div>
                          {hardware.owner_customer_id === selectedHardwareCustomer.id && (
                            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              Ägare
                            </span>
                          )}
                          {hardware.billing_customer_id === selectedHardwareCustomer.id && hardware.owner_customer_id !== selectedHardwareCustomer.id && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              Faktureringskund
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowCreateAssignmentModal(true);
                      const selectedHw = selectedHardware || hardwareResults.find(hw =>
                        (hw.owner_customer_id === selectedHardwareCustomer.id ||
                         hw.billing_customer_id === selectedHardwareCustomer.id)
                      );

                      let autoName = selectedHardwareCustomer.name;
                      if (selectedHw?.location) {
                        autoName += ` - ${selectedHw.location}`;
                      } else if (selectedHw?.name) {
                        autoName += ` - ${selectedHw.name}`;
                      }

                      setNewAssignmentForm({
                        name: autoName,
                        description: '',
                        caseNumber: '',
                        hardwareId: selectedHw?.id || ''
                      });
                    }}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Skapa nytt ärende
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        if (selectedHardware && onNavigateToCustomerWithHardware) {
                          onNavigateToCustomerWithHardware(selectedHardwareCustomer.id, selectedHardware.id);
                        }
                        setSelectedHardwareCustomer(null);
                        setSelectedHardware(null);
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Visa i kundregister
                    </button>
                    <button
                      onClick={() => {
                        setSelectedHardwareCustomer(null);
                        setSelectedHardware(null);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Stäng
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Assignment Modal */}
      {showCreateAssignmentModal && selectedHardwareCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Skapa Nytt Ärende</h3>
              <button
                onClick={() => {
                  setShowCreateAssignmentModal(false);
                  setNewAssignmentForm({ name: '', description: '', caseNumber: '', hardwareId: '' });
                  setSelectedHardwareCustomer(null);
                  setSelectedHardware(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={async (e) => {
                e.preventDefault();
                const nameValue = String(newAssignmentForm.name || '').trim();
                if (!onAddAssignment || !nameValue) {
                  console.error('Missing required data', { onAddAssignment: !!onAddAssignment, nameValue });
                  return;
                }

                try {
                  console.log('Creating assignment from Dashboard', {
                    customerId: selectedHardwareCustomer.id,
                    name: nameValue
                  });

                  const newAssignment = await onAddAssignment({
                    customerId: selectedHardwareCustomer.id,
                    name: nameValue,
                    description: String(newAssignmentForm.description || '').trim() || '',
                    caseNumber: String(newAssignmentForm.caseNumber || '').trim() || undefined,
                    hardwareId: newAssignmentForm.hardwareId || undefined,
                    createdDate: new Date().toISOString(),
                    status: 'active'
                  } as any);

                  console.log('Assignment created:', newAssignment);

                  if (newAssignment) {
                    setShowCreateAssignmentModal(false);
                    setNewAssignmentForm({ name: '', description: '', caseNumber: '', hardwareId: '' });
                    setSelectedHardwareCustomer(null);
                    setSelectedHardware(null);
                    alert(`Ärende "${newAssignment.name}" har skapats!`);
                  } else {
                    console.error('onAddAssignment returned undefined');
                    alert('Ett fel uppstod när ärendet skulle skapas. Inget ärende returnerades.');
                  }
                } catch (error) {
                  console.error('Error creating assignment:', error);
                  alert('Ett fel uppstod när ärendet skulle skapas. Vänligen försök igen.');
                }
              }} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900">
                    <strong>Kund:</strong> {selectedHardwareCustomer.name}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    <strong>Faktureringstyp:</strong>{' '}
                    {selectedHardwareCustomer.billing_type === 'time_bank' ? 'Timbank' :
                     selectedHardwareCustomer.billing_type === 'per_job' ? 'Per Jobb' : 'Fast pris'}
                  </p>
                </div>

                <div>
                  <label htmlFor="assignmentName" className="block text-sm font-medium text-gray-700 mb-1">
                    Ärendets namn *
                  </label>
                  <input
                    type="text"
                    id="assignmentName"
                    required
                    value={newAssignmentForm.name || ''}
                    onChange={(e) => setNewAssignmentForm({ ...newAssignmentForm, name: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    placeholder="t.ex. Service, Installation, Reparation"
                  />
                </div>

                <div>
                  <label htmlFor="assignmentDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Beskrivning
                  </label>
                  <textarea
                    id="assignmentDescription"
                    rows={3}
                    value={newAssignmentForm.description || ''}
                    onChange={(e) => setNewAssignmentForm({ ...newAssignmentForm, description: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    placeholder="Beskriv ärendet..."
                  />
                </div>

                <div>
                  <label htmlFor="assignmentCaseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Ärendenummer
                  </label>
                  <input
                    type="text"
                    id="assignmentCaseNumber"
                    value={newAssignmentForm.caseNumber || ''}
                    onChange={(e) => setNewAssignmentForm({ ...newAssignmentForm, caseNumber: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    placeholder="Ärendenummer från kundens system"
                  />
                </div>

                <div>
                  <label htmlFor="assignmentHardware" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Laptop className="h-4 w-4 mr-1 text-orange-600" />
                    Kopplad hårdvara
                  </label>
                  <select
                    id="assignmentHardware"
                    value={newAssignmentForm.hardwareId || ''}
                    onChange={(e) => setNewAssignmentForm({ ...newAssignmentForm, hardwareId: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                  >
                    <option value="">Ingen hårdvara kopplad</option>
                    {hardwareResults
                      .filter(hw =>
                        hw.owner_customer_id === selectedHardwareCustomer.id ||
                        hw.billing_customer_id === selectedHardwareCustomer.id
                      )
                      .map(hw => {
                        let displayText = '';
                        if (hw.model) {
                          displayText = hw.model;
                          if (hw.serial_number) {
                            displayText += ` • SN: ${hw.serial_number}`;
                          }
                        } else if (hw.name) {
                          displayText = hw.name;
                          if (hw.serial_number) {
                            displayText += ` • SN: ${hw.serial_number}`;
                          }
                        } else if (hw.serial_number) {
                          displayText = `SN: ${hw.serial_number}`;
                        } else {
                          displayText = hw.hardware_number || 'Okänd hårdvara';
                        }
                        return (
                          <option key={hw.id} value={hw.id}>
                            {displayText}
                          </option>
                        );
                      })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Vald hårdvara föreslås automatiskt vid tidsregistrering
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateAssignmentModal(false);
                      setNewAssignmentForm({ name: '', description: '', caseNumber: '', hardwareId: '' });
                      setSelectedHardwareCustomer(null);
                      setSelectedHardware(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2 inline" />
                    Skapa Ärende
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Active Timer Alert */}
      {activeTimer && (userSettings?.show_active_timer_alert ?? true) && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <Clock className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-900">
                  {activeTimer.type === 'work' ? '🕐 Arbetstid' :
                   activeTimer.type === 'travel' ? '🚗 Restid' : '🏠 Returresa'} pågår
                </p>
                <p className="text-sm text-red-700">
                  {activeTimer.customerName} • {activeTimer.assignmentName}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-red-600 font-mono">
                {currentElapsedTime}
              </p>
              <p className="text-sm text-red-600">
                Startad: {activeTimer.startTime}
              </p>
            </div>
          </div>
          <div className="mt-3">
            <button
              onClick={() => {
                // Navigate to register page to stop timer
                // Preserve existing form state but update timbank/assignment
                const existingState = localStorage.getItem('timbank_form_state');
                let formState: any = {
                  selectedCustomerId: activeTimer.customerId,
                  selectedAssignmentId: activeTimer.assignmentId
                };

                if (existingState) {
                  try {
                    const parsed = JSON.parse(existingState);
                    // Merge: keep existing data but override customer/assignment
                    formState = {
                      ...parsed,
                      selectedCustomerId: activeTimer.customerId,
                      selectedAssignmentId: activeTimer.assignmentId
                    };
                  } catch (error) {
                    console.error('Error parsing existing form state:', error);
                  }
                }

                localStorage.setItem('timbank_form_state', JSON.stringify(formState));
                onNavigateToRegister();
              }}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
            >
              <Play className="h-4 w-4 mr-2" />
              Gå till Timer för att stoppa
            </button>
          </div>
        </div>
      )}

      {/* Desktop Alerts */}
      <div className="hidden sm:block">
        {lowHourCustomers.length > 0 && (userSettings?.show_low_hours_warning ?? true) && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                <h4 className="font-medium text-yellow-900">Kunder med lågt saldo</h4>
              </div>
              <div className="mt-2 space-y-2">
                {lowHourCustomers.map((customer) => {
                  const remaining = (Number(customer.currentBalanceMinutes) || 0);
                  return (
                    <p key={customer.id} className="text-sm text-yellow-700">
                      <strong>{customer.name}</strong>: {formatHoursMinutes(remaining)} kvar
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Today's Jobs - Hidden on mobile */}
      {todaysJobsSummary.length > 0 && (
        <div className="hidden sm:block bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Dagens Jobb ({todaysJobsSummary.length})
              </h3>
              <span className="text-sm text-gray-500">
                {new Date().toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <div className="space-y-3">
              {todaysJobsSummary.map(({ assignment, customer, entries, totalMinutes }) => (
                <div
                  key={assignment.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {customer?.name || 'Ingen kund'}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600">{assignment.name}</span>
                        {assignment.status === 'completed' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Slutförd
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Aktiv
                          </span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        {entries.map((entry, index) => {
                          const workTime = entry.workStartTime && entry.workEndTime
                            ? calculateHoursFromTimes(entry.workStartTime, entry.workEndTime)
                            : (Number(entry.workTime) || 0);
                          const travelTime = entry.travelStartTime && entry.travelEndTime
                            ? calculateHoursFromTimes(entry.travelStartTime, entry.travelEndTime)
                            : (Number(entry.travelTime) || 0);
                          const returnTravelTime = Number(entry.returnTravelTime) || 0;

                          // Determine start time (earliest) and end time (latest)
                          const startTime = entry.travelStartTime || entry.workStartTime || '–';
                          const endTime = entry.returnTravelEndTime || entry.workEndTime || '–';

                          return (
                            <div key={entry.id} className="text-sm text-gray-600 hover:bg-gray-50 p-1 rounded group">
                              <div className="flex items-center gap-2">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {startTime} - {endTime}
                                </span>
                                {entry.location && (
                                  <>
                                    <MapPin className="h-3 w-3 ml-2" />
                                    <span>{entry.location}</span>
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    const sparePartsArray = Array.isArray(entry.spareParts)
                                      ? entry.spareParts
                                      : (entry.spareParts
                                        ? (typeof entry.spareParts === 'string'
                                          ? JSON.parse(entry.spareParts)
                                          : [entry.spareParts])
                                        : []);

                                    const formattedSpareParts = sparePartsArray.map((part: any) => ({
                                      quantity: part.quantity?.toString() || '1',
                                      partNumber: part.partNumber || part.part_number || '',
                                      description: part.description || '',
                                      price: part.price?.toString() || ''
                                    }));

                                    console.log('Original spareParts:', entry.spareParts);
                                    console.log('Formatted spareParts:', formattedSpareParts);

                                    setEditForm({
                                      ...entry,
                                      spareParts: formattedSpareParts
                                    });
                                  }}
                                  className="ml-auto opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-100 rounded transition-opacity"
                                  title="Redigera"
                                >
                                  <Edit2 className="h-3 w-3 text-blue-600" />
                                </button>
                              </div>
                              {(workTime > 0 || travelTime > 0 || returnTravelTime > 0 || (entry.parkingCost && entry.parkingCost > 0) || (entry.outbound_km && entry.outbound_km > 0) || (entry.return_km && entry.return_km > 0)) && (
                                <div className="ml-5 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                  {travelTime > 0 && (
                                    <span className="text-blue-600">
                                      Restid: {formatHoursMinutes(travelTime)}
                                    </span>
                                  )}
                                  {workTime > 0 && (
                                    <span className="text-gray-600">
                                      Arbetstid: {formatHoursMinutes(workTime)}
                                    </span>
                                  )}
                                  {returnTravelTime > 0 && (
                                    <span className="text-purple-600">
                                      Hemresa: {formatHoursMinutes(returnTravelTime)}
                                    </span>
                                  )}
                                  {entry.parkingCost && entry.parkingCost > 0 && (
                                    <span className="text-gray-500">
                                      Parkering: {entry.parkingCost} kr
                                    </span>
                                  )}
                                  {entry.outbound_km && entry.outbound_km > 0 && (
                                    <span className="text-gray-500">
                                      Resa: {entry.outbound_km} km
                                    </span>
                                  )}
                                  {entry.return_km && entry.return_km > 0 && (
                                    <span className="text-gray-500">
                                      Resa från: {entry.return_km} km
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-900">
                        {formatHoursMinutes(totalMinutes)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {entries.length} {entries.length === 1 ? 'post' : 'poster'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats - Hidden on mobile */}
      <div className="hidden sm:block bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Snabb Översikt
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.activeCustomers}</p>
              <p className="text-sm text-gray-500">Aktiva Kunder</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.totalAssignments}</p>
              <p className="text-sm text-gray-500">Totalt Uppdrag</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.activeAssignments}</p>
              <p className="text-sm text-gray-500">Aktiva Uppdrag</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.waitingForPartsAssignments}</p>
              <p className="text-sm text-gray-500">Väntar på Delar</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.completedAssignments}</p>
              <p className="text-sm text-gray-500">Slutförda</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{totalHardwareCount}</p>
              <p className="text-sm text-gray-500">Hårdvara</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600">{stats.totalEntries}</p>
              <p className="text-sm text-gray-500">Registreringar</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column: Active Assignments */}
        <div className="bg-white shadow rounded-lg lg:col-span-2">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Aktiva Uppdrag ({assignments.filter(a => a.status === 'active').length})
              </h3>

              {/* Sort Dropdown */}
              <div className="sm:w-56">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="date-desc">Nyaste först</option>
                  <option value="date-asc">Äldsta först</option>
                  <option value="hours-zero">0 h nyast</option>
                  <option value="hours-zero-oldest">0 h äldst</option>
                  <option value="hours-desc">Flest timmar först</option>
                  <option value="hours-asc">Minst timmar först</option>
                </select>
              </div>
            </div>
            {useMemo(() => {
              const activeAssignments = assignments.filter(a => a.status === 'active');

              // Calculate total hours for each assignment
              const getAssignmentHours = (assignmentId: string) => {
                const entries = timeEntries.filter(e => e.assignmentId === assignmentId);
                return entries.reduce((total, entry) =>
                  total + entry.workTime + entry.travelTime + (entry.returnTravelTime || 0), 0
                );
              };

              // Sort active assignments
              const sortedAssignments = [...activeAssignments].sort((a, b) => {
                switch (sortBy) {
                  case 'date-desc':
                    return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                  case 'date-asc':
                    return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                  case 'hours-zero':
                    const aHours = getAssignmentHours(a.id);
                    const bHours = getAssignmentHours(b.id);
                    if (aHours === 0 && bHours === 0) return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                    if (aHours === 0) return -1;
                    if (bHours === 0) return 1;
                    return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                  case 'hours-zero-oldest':
                    const aHoursOldest = getAssignmentHours(a.id);
                    const bHoursOldest = getAssignmentHours(b.id);
                    if (aHoursOldest === 0 && bHoursOldest === 0) return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                    if (aHoursOldest === 0) return -1;
                    if (bHoursOldest === 0) return 1;
                    return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                  case 'hours-desc':
                    return getAssignmentHours(b.id) - getAssignmentHours(a.id);
                  case 'hours-asc':
                    return getAssignmentHours(a.id) - getAssignmentHours(b.id);
                  default:
                    return 0;
                }
              });

              return sortedAssignments.length > 0 ? (
              <div className="space-y-1">
                {sortedAssignments.map((assignment) => {
                    const customer = customers.find(c => c.id === assignment.customerId);

                    const assignmentEntries = timeEntries.filter(entry => entry.assignmentId === assignment.id);
                    const totalHours = assignmentEntries.reduce((sum, entry) => {
                      const workTime = entry.workStartTime && entry.workEndTime
                        ? calculateHoursFromTimes(entry.workStartTime, entry.workEndTime)
                        : (Number(entry.workTime) || 0);
                      const travelTime = entry.travelStartTime && entry.travelEndTime
                        ? calculateHoursFromTimes(entry.travelStartTime, entry.travelEndTime)
                        : (Number(entry.travelTime) || 0);
                      return sum + workTime + travelTime + (Number(entry.returnTravelTime) || 0);
                    }, 0);

                  const isExpanded = expandedMobileAssignmentId === assignment.id;
                  const hasDescription = assignment.description || assignment.caseNumber;

                  return (
                    <div key={assignment.id} className="border-b border-gray-100 last:border-b-0">
                      <div
                        onMouseEnter={() => setHoveredAssignmentId(assignment.id)}
                        onMouseLeave={() => setHoveredAssignmentId(null)}
                        onClick={activeTimer ? undefined : (e) => {
                          // On mobile, toggle description expansion
                          if (window.innerWidth < 1024 && hasDescription) {
                            e.stopPropagation();
                            setExpandedMobileAssignmentId(isExpanded ? null : assignment.id);
                          } else {
                            // On desktop, navigate to register
                            const existingState = localStorage.getItem('timbank_form_state');
                            let formState: any = {
                              selectedCustomerId: assignment.customerId,
                              selectedAssignmentId: assignment.id,
                              newAssignmentName: '',
                              showNewAssignment: false
                            };

                            if (existingState) {
                              try {
                                const parsed = JSON.parse(existingState);
                                formState = {
                                  ...parsed,
                                  selectedCustomerId: assignment.customerId,
                                  selectedAssignmentId: assignment.id,
                                  newAssignmentName: '',
                                  showNewAssignment: false
                                };
                              } catch (error) {
                                console.error('Error parsing existing form state:', error);
                              }
                            }

                            localStorage.setItem('timbank_form_state', JSON.stringify(formState));
                            onNavigateToRegister();
                          }
                        }}
                        className={`w-full flex items-center justify-between py-2 transition-colors duration-200 rounded-lg px-2 -mx-2 ${
                          activeTimer
                            ? 'cursor-not-allowed opacity-50'
                            : 'hover:bg-blue-50 cursor-pointer'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900 text-left">
                              {customer?.name || 'Okänd kund'}
                            </span>
                            <span className="mx-2 text-gray-400">•</span>
                            <span className="text-sm text-gray-600 text-left">
                              {assignment.name}
                            </span>
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Aktiv
                            </span>
                            {hasDescription && (
                              <FileText className="h-4 w-4 text-gray-400 ml-2 lg:hidden" />
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {totalHours > 0 ? formatHoursMinutes(totalHours) : '0h'}
                        </div>
                      </div>

                      {/* Mobile Description Expansion */}
                      {isExpanded && hasDescription && (
                        <div className="lg:hidden px-2 pb-3 -mx-2">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                            {assignment.caseNumber && (
                              <div>
                                <p className="text-xs font-medium text-blue-700">Ärendenummer</p>
                                <p className="text-sm text-blue-900">{assignment.caseNumber}</p>
                              </div>
                            )}
                            {assignment.description && (
                              <div>
                                <p className="text-xs font-medium text-blue-700">Beskrivning</p>
                                <p className="text-sm text-blue-800 whitespace-pre-wrap">{assignment.description}</p>
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const existingState = localStorage.getItem('timbank_form_state');
                                let formState: any = {
                                  selectedCustomerId: assignment.customerId,
                                  selectedAssignmentId: assignment.id,
                                  newAssignmentName: '',
                                  showNewAssignment: false
                                };

                                if (existingState) {
                                  try {
                                    const parsed = JSON.parse(existingState);
                                    formState = {
                                      ...parsed,
                                      selectedCustomerId: assignment.customerId,
                                      selectedAssignmentId: assignment.id,
                                      newAssignmentName: '',
                                      showNewAssignment: false
                                    };
                                  } catch (error) {
                                    console.error('Error parsing existing form state:', error);
                                  }
                                }

                                localStorage.setItem('timbank_form_state', JSON.stringify(formState));
                                onNavigateToRegister();
                              }}
                              disabled={activeTimer}
                              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                activeTimer
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              <Clock className="h-4 w-4" />
                              Registrera tid
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Inga aktiva uppdrag</p>
              </div>
            );
            }, [assignments, timeEntries, sortBy, activeTimer, expandedMobileAssignmentId, onNavigateToRegister, customers])}
          </div>
        </div>

        {/* Right Column: Assignment Description */}
        <div className="hidden lg:block bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Beskrivning
            </h3>
            {hoveredAssignmentId ? (() => {
              const hoveredAssignment = assignments.find(a => a.id === hoveredAssignmentId);
              const customer = customers.find(c => c.id === hoveredAssignment?.customerId);
              return hoveredAssignment ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Kund</p>
                    <p className="text-sm text-gray-900">{customer?.name || 'Okänd kund'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Uppdrag</p>
                    <p className="text-sm text-gray-900">{hoveredAssignment.name}</p>
                  </div>
                  {hoveredAssignment.caseNumber && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Ärendenummer</p>
                      <p className="text-sm text-gray-900">{hoveredAssignment.caseNumber}</p>
                    </div>
                  )}
                  {hoveredAssignment.description && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Beskrivning</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{hoveredAssignment.description}</p>
                    </div>
                  )}
                </div>
              ) : null;
            })() : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Hovra över ett uppdrag för att se beskrivningen</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Other Status Groups */}
      <div className="hidden lg:grid grid-cols-1 gap-6 lg:grid-cols-2 mt-6">
        {['paused', 'waiting_parts', 'cancelled'].map(status => {
            const statusAssignments = assignments.filter(a => a.status === status);

            if (statusAssignments.length === 0) return null;

            const statusLabels: { [key: string]: { label: string; color: string } } = {
              paused: { label: 'Pausade Uppdrag', color: 'bg-yellow-100 text-yellow-800' },
              waiting_parts: { label: 'Väntar på Delar', color: 'bg-orange-100 text-orange-800' },
              cancelled: { label: 'Avbrutna Uppdrag', color: 'bg-red-100 text-red-800' }
            };

            const statusInfo = statusLabels[status];

            return (
              <div key={status} className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {statusInfo.label}
                    </h3>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}
                    >
                      {statusAssignments.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {statusAssignments.map((assignment) => {
                      const customer = customers.find(c => c.id === assignment.customerId);

                      const assignmentEntries = timeEntries.filter(entry => entry.assignmentId === assignment.id);
                      const totalHours = assignmentEntries.reduce((sum, entry) => {
                        const workTime = entry.workStartTime && entry.workEndTime
                          ? calculateHoursFromTimes(entry.workStartTime, entry.workEndTime)
                          : (Number(entry.workTime) || 0);
                        const travelTime = entry.travelStartTime && entry.travelEndTime
                          ? calculateHoursFromTimes(entry.travelStartTime, entry.travelEndTime)
                          : (Number(entry.travelTime) || 0);
                        return sum + workTime + travelTime + (Number(entry.returnTravelTime) || 0);
                      }, 0);

                      return (
                        <div
                          key={assignment.id}
                          className="w-full flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <User className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900 text-left">
                                {customer?.name || 'Okänd kund'}
                              </span>
                              <span className="mx-2 text-gray-400">•</span>
                              <span className="text-sm text-gray-600 text-left">
                                {assignment.name}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {totalHours > 0 ? formatHoursMinutes(totalHours) : '0h'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      {/* Active Customers Status - Bottom of page - Hidden on mobile */}
      <div className="hidden sm:block bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Kunder (Visar 5 mest aktiva av {customers.filter(c => c.status === 'active').length})
          </h3>

          <div className="space-y-4">
            {activeCustomers.map((customer) => {
              const balanceMinutes = customer.currentBalanceMinutes || 0;
              const usagePercentage = customer.totalPurchasedMinutes > 0
                ? (customer.totalUsedMinutes / customer.totalPurchasedMinutes) * 100
                : 0;

              const isPerJob = customer.billingType === 'per_job';
              const customerEntries = isPerJob
                ? timeEntries.filter(e => e.customerId === customer.id)
                : [];
              const unbilledEntries = customerEntries.filter(e => !e.isInvoiced);
              const billedEntries = customerEntries.filter(e => e.isInvoiced);
              const unbilledCount = unbilledEntries.length;
              const billedCount = billedEntries.length;
              const totalJobs = customerEntries.length;

              return (
                <div
                  key={customer.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-500" />
                        {customer.name}
                      </h4>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {customer.createdAt && !isNaN(new Date(customer.createdAt).getTime())
                          ? new Date(customer.createdAt).toLocaleDateString('sv-SE')
                          : 'Ogiltigt datum'
                        }
                        {customer.updatedAt && (
                          <>
                            <span className="mx-2 text-gray-300">•</span>
                            <span className="text-xs text-gray-400">
                              Uppdaterad: {new Date(customer.updatedAt).toLocaleDateString('sv-SE')} {new Date(customer.updatedAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Aktiv
                      </span>
                      {!isPerJob && balanceMinutes < 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {formatHoursMinutes(Math.abs(balanceMinutes))} över
                        </span>
                      )}
                      {isPerJob && (
                        <>
                          {unbilledCount > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {unbilledCount} ofakturerade
                            </span>
                          )}
                          {billedCount > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {billedCount} fakturerade
                            </span>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => {
                          localStorage.setItem('selected_customer_for_report', customer.id);
                          window.location.hash = 'reports';
                        }}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title="Visa rapport för denna kund"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {isPerJob ? (
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Ofakturerade</p>
                        <p className={`text-lg font-semibold ${unbilledCount > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                          {unbilledCount} st
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Fakturerade</p>
                        <p className="text-lg font-semibold text-blue-600">
                          {billedCount} st
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Totalt</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {totalJobs} st
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4 sm:grid-cols-3">
                        <div>
                          <p className="text-sm text-gray-500">Totalt köpt</p>
                          <p className="text-lg font-semibold text-gray-900">{formatHoursMinutes(customer.totalPurchasedMinutes)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Använt</p>
                          <p className="text-lg font-semibold text-orange-600">{formatHoursMinutes(customer.totalUsedMinutes)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Saldo</p>
                          <p className={`text-lg font-semibold ${balanceMinutes >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatHoursMinutes(Math.abs(balanceMinutes))}
                            {balanceMinutes < 0 && ' över'}
                          </p>
                        </div>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${
                            usagePercentage > 80 ? 'bg-red-500' : usagePercentage > 60 ? 'bg-orange-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(isNaN(usagePercentage) ? 0 : usagePercentage, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {isNaN(usagePercentage) ? '0.0' : usagePercentage.toFixed(1)}% använt
                      </p>
                    </>
                  )}
                </div>
              );
            })}

            {activeCustomers.length === 0 && (
              <div className="text-center py-12">
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Inga aktiva kunder</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Entry Modal */}
      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          assignment={assignments.find(a => a.id === editingEntry.assignmentId)}
          assignments={assignments}
          editForm={editForm as any}
          sparePartsHistory={sparePartsHistory}
          availableArticles={availableArticles}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          onDelete={() => {
            if (window.confirm('Är du säker på att du vill ta bort denna tidspost?')) {
              onDeleteTimeEntry(editingEntry.id);
              setEditingEntry(null);
              setEditForm(null);
            }
          }}
          onFormChange={handleFormChange}
          onUpdateAssignment={handleUpdateAssignment}
        />
      )}

      {/* Quick Assignment Modal */}
      {showQuickAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Välj Kund för Nytt Uppdrag</h3>
                <button
                  onClick={() => {
                    setShowQuickAssignmentModal(false);
                    setQuickAssignmentSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Sök kund..."
                  value={quickAssignmentSearchQuery}
                  onChange={(e) => setQuickAssignmentSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {customers.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Inga kunder tillgängliga</p>
                ) : (
                  customers
                    .filter(c => c.status === 'active')
                    .filter(c => {
                      if (!quickAssignmentSearchQuery.trim()) return true;
                      const query = quickAssignmentSearchQuery.toLowerCase();
                      return c.name.toLowerCase().includes(query) ||
                             c.email?.toLowerCase().includes(query) ||
                             c.organizationNumber?.toLowerCase().includes(query);
                    })
                    .sort((a, b) => {
                      const aScore = customerActivityScores.get(a.id) || 0;
                      const bScore = customerActivityScores.get(b.id) || 0;
                      if (aScore !== bScore) {
                        return bScore - aScore;
                      }
                      return a.name.localeCompare(b.name, 'sv');
                    })
                    .map(customer => (
                      <button
                        key={customer.id}
                        onClick={() => {
                          setSelectedHardwareCustomer(customer);
                          setShowQuickAssignmentModal(false);
                          setQuickAssignmentSearchQuery('');
                          setShowCreateAssignmentModal(true);
                        }}
                        className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        <div className="text-sm text-gray-600">
                          Kvarvarande: {formatHoursMinutes(customer.currentBalanceMinutes || 0)}
                        </div>
                      </button>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});